import { useMemo } from 'react';
import { ToolCallCard } from './ToolCallCard';
import { SubAgentCard } from './SubAgentCard';
import { buildEventTree, type OrchestratorEvent } from './useOrchestratorStream';

interface ExecutionTimelineProps {
  events: OrchestratorEvent[];
  streamingText: string;
  isRunning: boolean;
}

export const ExecutionTimeline = ({ events, streamingText, isRunning }: ExecutionTimelineProps) => {
  const { topLevel, subAgentEvents } = useMemo(() => buildEventTree(events), [events]);

  const renderedCallIds = new Set<string>();

  return (
    <div className="flex flex-col gap-1 p-3">
      {topLevel.map((evt) => {
        switch (evt.type) {
          case 'thinking':
            return (
              <div key={evt.id} className="flex items-center gap-2 py-1">
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                <span className="text-xs text-muted-foreground">
                  Orchestrator thinking...
                </span>
              </div>
            );

          case 'tool_call': {
            const toolName = evt.data.toolName as string;

            // If this tool call is a sub-agent invocation, render it as SubAgentCard.
            const matchingSubAgentEvents = [...subAgentEvents.entries()].find(
              ([, evts]) => evts.some((e) => e.type === 'sub_agent_started')
            );

            if (matchingSubAgentEvents) {
              const [callId, callEvents] = matchingSubAgentEvents;
              if (!renderedCallIds.has(callId)) {
                renderedCallIds.add(callId);
                return (
                  <SubAgentCard
                    key={`sub-${callId}`}
                    callId={callId}
                    events={callEvents}
                    allEvents={subAgentEvents}
                  />
                );
              }
            }

            return (
              <ToolCallCard
                key={evt.id}
                toolName={toolName}
                args={evt.data.arguments as string}
              />
            );
          }

          case 'response_complete':
            return (
              <div
                key={evt.id}
                className="whitespace-pre-wrap break-words rounded border border-border bg-card p-3 text-sm"
              >
                {evt.data.response as string}
              </div>
            );

          default:
            return null;
        }
      })}

      {/* Render any sub-agent call groups not already shown inline */}
      {[...subAgentEvents.entries()]
        .filter(([callId]) => !renderedCallIds.has(callId))
        .map(([callId, callEvents]) => (
          <SubAgentCard
            key={`sub-${callId}`}
            callId={callId}
            events={callEvents}
            allEvents={subAgentEvents}
          />
        ))}

      {isRunning && streamingText && (
        <div
          className="whitespace-pre-wrap break-words rounded border border-blue-400 bg-card p-3 text-sm"
        >
          {streamingText}
          <span className="animate-pulse">|</span>
        </div>
      )}
    </div>
  );
};
